import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosApiNoAuth from '../axiosApiNoAuth';

/**
 * DataVersionDisplay Component
 * 
 * Displays the current data version dynamically fetched from the API.
 * Supports two display variants: inline (for text areas) and overlay (for maps).
 * 
 * @param {Object} props
 * @param {string} props.variant - Display variant: 'inline' or 'overlay' (default: 'inline')
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showLabel - Show "Data Version:" label (default: true)
 * @param {string} props.dataType - Type of data to fetch (default: 'taxdata')
 * 
 * @example
 * // Inline display (text areas)
 * <DataVersionDisplay variant="inline" />
 * 
 * // Overlay display (map)
 * <DataVersionDisplay variant="overlay" className="bottom-4 left-4" />
 * 
 * // Without label
 * <DataVersionDisplay showLabel={false} />
 */
const DataVersionDisplay = ({ 
  variant = 'inline', 
  className = '', 
  showLabel = true,
  dataType = 'taxdata'
}) => {
  const navigate = useNavigate();
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleClick = () => {
    if (variant === 'overlay') {
      navigate('/download-data-filter-form');
    }
  };

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('[DataVersionDisplay] Fetching from:', `/api/dataversion/current/?type=${dataType}`);
        const response = await axiosApiNoAuth.get(`/api/dataversion/current/?type=${dataType}`);
        console.log('[DataVersionDisplay] Response:', response.data);
        
        if (response.data && response.data.version_display) {
          setVersion(response.data.version_display);
          console.log('[DataVersionDisplay] Version set to:', response.data.version_display);
        } else {
          throw new Error('Invalid version data format');
        }
      } catch (err) {
        console.error('[DataVersionDisplay] Error fetching data version:', err);
        console.error('[DataVersionDisplay] Error details:', err.response?.data || err.message);
        setError(err.message);
        // Fallback to null - component will not display anything
        setVersion(null);
      } finally {
        setLoading(false);
      }
    };

    fetchVersion();
  }, [dataType]);

  // Overlay variant (for maps) - show loading/error state for debugging
  if (variant === 'overlay') {
    if (loading) {
      return (
        <div className={`bg-white bg-opacity-90 px-2 py-1 shadow-sm text-xs ${className}`}>
          <span className="text-gray-700">Loading version...</span>
        </div>
      );
    }
    
    if (error || !version) {
      return (
        <div className={`bg-red-100 bg-opacity-90 px-2 py-1 shadow-sm text-xs ${className}`}>
          <span className="text-red-700">Data Version: Error loading</span>
        </div>
      );
    }
    
    return (
      <div 
        onClick={handleClick}
        className={`bg-white bg-opacity-90 px-2 py-1 shadow-sm text-xs cursor-pointer transition-colors hover:bg-ccre-orange-900 hover:text-white ${className}`}
      >
        {showLabel && <span className="hover:text-white">Data Version: </span>}
        <span className="hover:text-white">{version}</span>
      </div>
    );
  }

  // Don't render anything while loading or if there's an error (for inline variant)
  if (loading || error || !version) {
    return null;
  }

  // Inline variant (for text areas)
  if (variant === 'inline') {
    return (
      <>
        {showLabel && <strong>Data Version</strong>}
        {showLabel && ': '}
        {version}
      </>
    );
  }

  // Default fallback
  return (
    <span className={className}>
      {showLabel && 'Data Version: '}
      {version}
    </span>
  );
};

export default DataVersionDisplay;
